/**
 * src/shared/hooks/usePermissions.js
 *
 * Returns a `can(permission)` function based on the current admin's role.
 * Reads role from AuthContext — no extra API call needed.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   if (can('disable_users')) { ... }
 *   // or with constant:
 *   if (can(PERMISSIONS.DISABLE_USERS)) { ... }
 */

import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ROLE_PERMISSIONS } from '@/shared/constants/permissions';

export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role ?? '';

  const permissions = useMemo(() => {
    if (role === 'super_admin') return null; // null = all permissions
    return new Set(ROLE_PERMISSIONS[role] ?? []);
  }, [role]);

  const can = useMemo(() => (permission) => {
    if (!permission) return false;
    if (!user) return false;
    if (role === 'super_admin') return true;
    return permissions?.has(permission) ?? false;
  }, [permissions, role, user]);

  return { can, role, permissions };
}
